import { ExtractFromSegmentProfiler } from "@geoblocks/edittrack";
import { pointerMove } from "ol/events/condition";
import type Feature from "ol/Feature";
import type LineString from "ol/geom/LineString";
import type Point from "ol/geom/Point";
import Select from "ol/interaction/Select";
import VectorLayer from "ol/layer/Vector";
import type OLMap from "ol/Map";
import VectorSource from "ol/source/Vector";
import type View from "ol/View";
import { fetchHiking, type HikingType, hikingTypes, readFeatures } from "./api-hiking";
import { getProfile, type HikingProfile } from "./ElevationProfile";
import { TRACK_Z_INDEX } from "./style/common";
import { trackStyle } from "./style/hiking";
import { enumerate, indexToLabel, splitFeaturesByProperty } from "./utils";

export type HikingViewerOptions = {
  /**
   * An OpenLayers map instance
   */
  map: OLMap;
  /**
   * The type of the hiking to display
   */
  type: HikingType;
  /**
   * The uuid of the hiking to display
   */
  uuid?: string;
};

export default class HikingViewer extends EventTarget {
  private hikingType!: HikingType;
  private uuid?: string;
  private view: View;
  private trackSource: VectorSource;
  private profiler: ExtractFromSegmentProfiler;
  constructor(options: HikingViewerOptions) {
    super();

    this.trackSource = new VectorSource();
    const trackLayer = new VectorLayer({
      source: this.trackSource,
      style: trackStyle,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    options.map.addLayer(trackLayer);

    this.view = options.map.getView();

    this.profiler = new ExtractFromSegmentProfiler({
      projection: this.view.getProjection(),
    });

    this.setType(options.type);
    this.loadHiking(options.uuid);

    const select = new Select({
      condition: pointerMove,
      style: null,
      multi: false,
      filter: (feature) => isStopOver(feature),
    });
    options.map.addInteraction(select);

    select.on("select", (event) => {
      // note: we are sure that the length of selected and deselected is 1 because 'multi' is false in the constructor options
      if (event.selected.length === 1) {
        const feature = event.selected[0];
        this.dispatchEvent(new CustomEvent("pointerenter", { detail: { feature: feature } }));
        this.hoverFeature(feature);
      }
      if (event.deselected.length === 1) {
        const feature = event.deselected[0];
        this.dispatchEvent(new CustomEvent("pointerleave", { detail: { feature: feature } }));
        this.unhoverFeature(feature);
      }
    });
  }

  /**
   * Set the type of the hiking. The type can be `hiking_activity` or `hiking_proposition`.
   */
  public setType(type: HikingType) {
    if (!hikingTypes.includes(type)) {
      throw new Error(`invalid hiking type: '${type}'`);
    }
    this.hikingType = type;
  }

  /**
   * Load the hiking from the server.
   */
  public async loadHiking(uuid: string | undefined): Promise<void> {
    this.uuid = uuid;
    if (this.uuid) {
      const hiking = await fetchHiking(this.uuid, this.hikingType);
      if (hiking.coordinates != null && hiking.control_points_with_stopovers != null) {
        // only keep stopovers and segments
        const features = readFeatures(hiking, this.view.getProjection());
        const segments = features.filter(isSegment) as Feature<LineString>[];
        for (const [index, segment] of enumerate(segments)) {
          segment.set("index", index);
          segment.set("zIndex", TRACK_Z_INDEX);
          this.profiler.computeProfile(segment);
        }
        const stopovers = features.filter(isStopOver) as Feature<Point>[];
        for (const [index, stopover] of enumerate(stopovers)) {
          stopover.set("label", indexToLabel(index));
          stopover.set("zIndex", TRACK_Z_INDEX + stopovers.length - index);
        }
        // remove labels from the first and last point
        stopovers.at(0)?.set("label", "");
        stopovers.at(-1)?.set("label", "");

        this.trackSource.clear();
        this.trackSource.addFeatures([...segments, ...stopovers]);
        this.view.fit(this.trackSource.getExtent(), {
          padding: [30, 30, 30, 30],
        });
        this.dispatchEvent(new CustomEvent("loaded"));
      }
    }
  }

  public getStopovers(): Feature<Point>[] {
    return this.trackSource.getFeatures().filter(isStopOver).sort(byPartAndIndex) as Feature<Point>[];
  }

  public getProfile(): HikingProfile {
    const features = this.trackSource.getFeatures();
    const segments = features.filter(isSegment).sort(byPartAndIndex) as Feature<LineString>[];
    const stopovers = features.filter(isStopOver).sort(byPartAndIndex) as Feature<Point>[];

    return getProfile(
      splitFeaturesByProperty(segments, "part"),
      stopovers.map((stopover) => [stopover.get("part"), stopover.get("index")]),
    );
  }

  public hoverFeature(feature: Feature) {
    feature.set("highlight", true);
  }

  public unhoverFeature(feature: Feature) {
    feature.set("highlight", false);
  }
}

function isSegment(feature: Feature) {
  return feature.get("type") === "segment";
}

function isStopOver(feature: Feature) {
  return feature.get("type") === "controlPoint" && feature.get("sww_name");
}

function byPartAndIndex(a: Feature, b: Feature) {
  return a.get("part") - b.get("part") || a.get("index") - b.get("index");
}
